import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, KeyRound, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { navigateWithTransition } from "@/lib/viewTransition";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";

const FEATURES = [
  { icon: KeyRound, label: "Enlace de un solo uso, caduca a los 30 minutos" },
  { icon: ShieldCheck, label: "Nunca decimos si un email existe o no en el sistema" },
];

function BackToLoginLink() {
  const navigate = useNavigate();
  return (
    <a
      href="/login"
      onClick={(e) => {
        e.preventDefault();
        navigateWithTransition(navigate, "/login");
      }}
      className="group inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-0.5" />
      Volver al inicio de sesión
    </a>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } finally {
      // Siempre mostramos el mismo mensaje de éxito, exista o no la cuenta: evita que se pueda
      // usar este formulario para averiguar qué emails están dados de alta.
      setLoading(false);
      setSent(true);
    }
  }

  return (
    <AuthLayout
      headline="¿Has olvidado tu contraseña?"
      subtext="Te mandamos un enlace para elegir una nueva. Solo tú puedes usarlo, y caduca a los 30 minutos."
      features={FEATURES}
      formEyebrow={
        !sent && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
            <Sparkles className="h-3.5 w-3.5" />
            Recuperar acceso
          </span>
        )
      }
    >
      {sent ? (
        <div className="flex flex-col items-start gap-4">
          <span className="auth-in flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <h2 className="auth-in text-2xl font-bold text-ink-900" style={{ animationDelay: "60ms" }}>
            Revisa tu correo
          </h2>
          <p className="auth-in text-sm text-ink-500" style={{ animationDelay: "120ms" }}>
            Si <strong>{email}</strong> tiene una cuenta en PuntoPadel, te hemos enviado un enlace para
            restablecer la contraseña. Revisa también la carpeta de spam.
          </p>
          <div className="auth-in" style={{ animationDelay: "180ms" }}>
            <BackToLoginLink />
          </div>
        </div>
      ) : (
        <>
          <h2 className="auth-in text-2xl font-bold text-ink-900">Recupera tu contraseña</h2>
          <p className="auth-in mt-1 mb-8 text-sm text-ink-400" style={{ animationDelay: "60ms" }}>
            Introduce el email con el que accedes al panel.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="auth-in flex flex-col gap-1.5" style={{ animationDelay: "120ms" }}>
              <label htmlFor="email" className="text-sm font-medium text-ink-600">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="tucorreo@club.com"
                className="w-full rounded-xl border border-ink-100 bg-white px-3 py-2.5 text-sm shadow-sm outline-none transition-all duration-200 hover:border-ink-200 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="auth-in mt-2 w-full"
              style={{ animationDelay: "180ms" }}
            >
              {loading ? "Enviando…" : "Enviar enlace de recuperación"}
            </Button>
          </form>

          <p className="auth-in mt-6 text-center text-sm" style={{ animationDelay: "240ms" }}>
            <BackToLoginLink />
          </p>
        </>
      )}
    </AuthLayout>
  );
}
